import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { ChurchBrandMark } from '@/components/church-brand-mark';
import { KioskQrCanvas } from '@/components/kiosk-qr-canvas';
import { CheckinPageShell } from '@/components/checkin-page-shell';
import { PoweredByFooter } from '@/components/powered-by-footer';
import { api, ApiError } from '@/lib/api';
import {
  GeolocationDeniedError,
  GeolocationUnavailableError,
  hasLocationConsentAck,
  queryGeolocationPermission,
  readDevicePosition,
  writeLocationConsentAck,
} from '@/lib/device-location';
import {
  clearDisplayToken,
  consumeAccessFromUrl,
  readDisplayToken,
  readGatewayAuth,
  writeDisplayToken,
  writeGatewayAuth,
  type KioskGatewayAuth,
} from '@/lib/gateway-auth';

interface KioskRuntimeState {
  kiosk: { id: string; name: string };
  church?: { name: string; campusName: string; logoUrl: string | null } | null;
  session: {
    id: string;
    headcount: number;
    status: string;
    opensAt: string;
    closesAt: string;
  } | null;
  display: {
    token: string;
    expiresAt: string;
    rotationSeconds: number;
  } | null;
}

interface PairingStatus {
  kiosk: { id: string; name: string; status: string; churchId: string };
  church: { name: string; campusName: string; logoUrl: string | null };
  accessToken: string | null;
}

type LocationBlock = 'denied' | 'unavailable' | null;

const GPS_RETRY_MS = 5_000;

const RECOVERABLE_ERROR_CODES = new Set([
  'ATTENDANCE_GPS_ACCURACY_TOO_LOW',
  'ATTENDANCE_GPS_OUTSIDE_RADIUS',
  'ATTENDANCE_SESSION_NOT_OPEN',
  'GEOLOCATION_UNAVAILABLE',
  'UNEXPECTED',
]);

const isRecoverableErrorCode = (code: string | null): boolean =>
  code !== null && RECOVERABLE_ERROR_CODES.has(code);

const isRecoverableError = (error: unknown): boolean => {
  if (error instanceof GeolocationUnavailableError) return true;
  if (error instanceof GeolocationDeniedError) return false;
  if (error instanceof ApiError) {
    if (error.code === 'KIOSK_REVOKED' || error.code === 'PLAN_UPGRADE_REQUIRED') {
      return false;
    }
    if (error.code === 'ATTENDANCE_GPS_CENTRE_REQUIRED') return false;
    return isRecoverableErrorCode(error.code ?? 'UNEXPECTED');
  }
  return true;
};

export const DisplayScreen = () => {
  const { t } = useTranslation();
  const { pairingCode = '' } = useParams();
  const [auth, setAuth] = useState<KioskGatewayAuth | null>(() =>
    consumeAccessFromUrl(pairingCode) ?? readGatewayAuth(pairingCode),
  );
  const [church, setChurch] = useState<PairingStatus['church'] | null>(null);
  const [runtime, setRuntime] = useState<KioskRuntimeState | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [locationBlock, setLocationBlock] = useState<LocationBlock>(null);
  const [awaitingLocationConsent, setAwaitingLocationConsent] = useState(false);
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const hasDisplayedQr = useRef(false);

  useEffect(() => {
    if (!pairingCode) return;
    void api
      .get<PairingStatus>(`/gateway/attendance/kiosks/${encodeURIComponent(pairingCode)}`)
      .then((data) => setChurch(data.church))
      .catch(() => undefined);
  }, [pairingCode]);

  const ensureAuth = useCallback(async (): Promise<KioskGatewayAuth | null> => {
    const fromUrl = consumeAccessFromUrl(pairingCode);
    const existing = fromUrl ?? readGatewayAuth(pairingCode);
    if (existing) {
      setAuth(existing);
      return existing;
    }

    try {
      const status = await api.get<PairingStatus>(
        `/gateway/attendance/kiosks/${encodeURIComponent(pairingCode)}`,
      );
      setChurch(status.church);
      if (status.kiosk.status !== 'paired' || !status.accessToken) {
        return null;
      }
      const next: KioskGatewayAuth = {
        accessToken: status.accessToken,
        pairingCode,
        kioskId: status.kiosk.id,
        churchId: status.kiosk.churchId,
      };
      writeGatewayAuth(next);
      setAuth(next);
      return next;
    } catch {
      setErrorCode('UNEXPECTED');
      return null;
    }
  }, [pairingCode]);

  const applyError = useCallback((error: unknown, recoverable: boolean) => {
    if (error instanceof GeolocationDeniedError) {
      setLocationBlock('denied');
      setLocationEnabled(false);
      setErrorCode(null);
      clearDisplayToken(pairingCode);
      return;
    }

    if (error instanceof GeolocationUnavailableError) {
      if (recoverable && hasDisplayedQr.current) {
        setErrorCode('GEOLOCATION_UNAVAILABLE');
        return;
      }
      setLocationBlock('unavailable');
      setLocationEnabled(false);
      setErrorCode(null);
      clearDisplayToken(pairingCode);
      return;
    }

    if (error instanceof ApiError) {
      if (error.code === 'KIOSK_REVOKED') {
        setErrorCode('KIOSK_REVOKED');
        clearDisplayToken(pairingCode);
        return;
      }
      if (error.code === 'PLAN_UPGRADE_REQUIRED') {
        setErrorCode('PLAN_UPGRADE_REQUIRED');
        clearDisplayToken(pairingCode);
        return;
      }
      setErrorCode(error.code ?? 'UNEXPECTED');
      if (!recoverable) {
        clearDisplayToken(pairingCode);
      }
      return;
    }

    setErrorCode('UNEXPECTED');
    if (!recoverable) {
      clearDisplayToken(pairingCode);
    }
  }, [pairingCode]);

  const refreshRuntime = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      const token = auth ?? (await ensureAuth());
      if (!token) {
        setInitialLoading(false);
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else if (!hasDisplayedQr.current) {
        setInitialLoading(true);
      }

      try {
        const position = await readDevicePosition();
        setLocationBlock(null);
        writeLocationConsentAck(pairingCode);

        const state = await api.get<KioskRuntimeState>(
          '/gateway/attendance/kiosk-state',
          token.accessToken,
          position,
        );
        setRuntime(state);
        if (state.church) setChurch(state.church);
        setErrorCode(null);
        if (state.display?.token) {
          writeDisplayToken(pairingCode, state.display.token);
          hasDisplayedQr.current = true;
        } else if (!silent || !hasDisplayedQr.current) {
          clearDisplayToken(pairingCode);
        }
      } catch (error) {
        const recoverable = isRecoverableError(error);
        if (!recoverable || !hasDisplayedQr.current) {
          clearDisplayToken(pairingCode);
          setRuntime((current) =>
            current ? { ...current, display: null, session: current.session } : current,
          );
        }
        applyError(error, recoverable);
      } finally {
        setRefreshing(false);
        setInitialLoading(false);
      }
    },
    [applyError, auth, ensureAuth, pairingCode],
  );

  const enableLocation = useCallback(() => {
    setInitialLoading(true);
    setLocationBlock(null);
    setErrorCode(null);
    setAwaitingLocationConsent(false);
    setLocationEnabled(true);
    void refreshRuntime();
  }, [refreshRuntime]);

  useEffect(() => {
    void ensureAuth();
  }, [ensureAuth]);

  useEffect(() => {
    if (!auth) {
      setAwaitingLocationConsent(false);
      setLocationEnabled(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      const permission = await queryGeolocationPermission();
      if (cancelled) return;

      if (permission === 'granted' || hasLocationConsentAck(pairingCode)) {
        writeLocationConsentAck(pairingCode);
        setAwaitingLocationConsent(false);
        setLocationEnabled(true);
        setLocationBlock(null);
        return;
      }

      if (permission === 'denied') {
        setAwaitingLocationConsent(false);
        setLocationEnabled(false);
        setLocationBlock('denied');
        setInitialLoading(false);
        return;
      }

      if (permission === 'unsupported') {
        setAwaitingLocationConsent(false);
        setLocationEnabled(false);
        setLocationBlock('unavailable');
        setInitialLoading(false);
        return;
      }

      // "prompt" — first visit on this device: explain before the browser dialog.
      setAwaitingLocationConsent(true);
      setLocationEnabled(false);
      setInitialLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [auth, pairingCode]);

  useEffect(() => {
    if (!auth || !locationEnabled) return;
    void refreshRuntime();
    const rotationMs = (runtime?.display?.rotationSeconds ?? 15) * 1000;
    const timer = setInterval(() => void refreshRuntime({ silent: true }), rotationMs);
    return () => clearInterval(timer);
  }, [auth, locationEnabled, refreshRuntime, runtime?.display?.rotationSeconds]);

  useEffect(() => {
    if (!auth || !locationEnabled || !errorCode) return;
    if (!isRecoverableErrorCode(errorCode)) return;

    const timer = setTimeout(() => void refreshRuntime({ silent: true }), GPS_RETRY_MS);
    return () => clearTimeout(timer);
  }, [auth, errorCode, locationEnabled, refreshRuntime]);

  useEffect(() => {
    if (!auth) return;
    const timer = setInterval(
      () => void api.post('/gateway/attendance/kiosks/heartbeat', {}, auth.accessToken),
      60_000,
    );
    return () => clearInterval(timer);
  }, [auth]);

  const displayToken =
    locationBlock || awaitingLocationConsent
      ? null
      : runtime?.display?.token ?? readDisplayToken(pairingCode);

  const showLocationConsent = Boolean(auth && awaitingLocationConsent);
  const showLocationPrompt = Boolean(auth && locationBlock);
  const showInitialLoading =
    auth && initialLoading && !displayToken && !showLocationConsent && !showLocationPrompt;

  const showBlockingError =
    Boolean(errorCode) &&
    !displayToken &&
    !showLocationConsent &&
    !showLocationPrompt;

  const waitingForSession =
    auth &&
    locationEnabled &&
    !initialLoading &&
    !locationBlock &&
    !showBlockingError &&
    runtime !== null &&
    !runtime.session &&
    !displayToken;

  const showSessionRequired =
    !displayToken &&
    auth &&
    locationEnabled &&
    !initialLoading &&
    !showBlockingError &&
    !locationBlock &&
    !waitingForSession;

  const showRecoverableSync =
    Boolean(displayToken) &&
    (refreshing || (errorCode !== null && isRecoverableErrorCode(errorCode)));

  return (
    <CheckinPageShell footer={<PoweredByFooter />}>
      <section
        className={`checkin-card checkin-card--display${showRecoverableSync ? ' checkin-card--syncing' : ''}`}
        aria-busy={refreshing}
      >
        {church ? (
          <ChurchBrandMark
            churchName={church.name}
            campusName={church.campusName}
            logoUrl={church.logoUrl}
            title={displayToken ? t('display.title') : runtime?.kiosk.name ?? t('display.title')}
            description={displayToken ? t('display.title') : null}
          />
        ) : (
          <h1 className="church-brand-title">{t('display.title')}</h1>
        )}

        {!auth ? (
          <p className="checkin-card-note">{t('pairing.waiting')}</p>
        ) : null}

        {showLocationConsent ? (
          <div className="checkin-prompt-block">
            <p className="checkin-card-note">{t('display.locationRequired')}</p>
            <button type="button" className="checkin-primary-button" onClick={enableLocation}>
              {t('display.locationEnable')}
            </button>
          </div>
        ) : null}

        {showLocationPrompt ? (
          <div className="checkin-prompt-block">
            <p className="checkin-card-note">
              {locationBlock === 'denied'
                ? t('display.locationDenied')
                : t('display.locationUnavailable')}
            </p>
            <button type="button" className="checkin-primary-button" onClick={enableLocation}>
              {t('display.locationEnable')}
            </button>
          </div>
        ) : null}

        {showInitialLoading ? (
          <p className="checkin-card-note">{t('display.loading')}</p>
        ) : null}

        {showBlockingError ? (
          <div className="checkin-prompt-block">
            <p className="checkin-error">{t(`errors.${errorCode}`, t('error'))}</p>
          </div>
        ) : null}

        {waitingForSession ? (
          <p className="checkin-card-note">{t('display.waitingForSession')}</p>
        ) : null}

        {showSessionRequired ? (
          <p className="checkin-card-note">{t('display.sessionRequiredLead')}</p>
        ) : null}

        {displayToken ? (
          <div className="checkin-qr-wrap">
            <KioskQrCanvas value={displayToken} size={320} label={t('display.title')} />
          </div>
        ) : null}

        {runtime?.session ? (
          <p className="checkin-headcount">
            {t('display.headcount')}: <strong>{runtime.session.headcount}</strong>
          </p>
        ) : null}

        {displayToken ? (
          <div className="checkin-display-actions">
            <Link to={`/guest/${encodeURIComponent(pairingCode)}`} className="checkin-guest-link">
              {t('guest.cta')}
            </Link>
          </div>
        ) : null}

        {showRecoverableSync ? (
          <div className="checkin-card-sync-bar" aria-hidden="true">
            <span className="checkin-card-sync-bar__pulse" />
          </div>
        ) : null}
      </section>
    </CheckinPageShell>
  );
};
